import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  MenuItem,
  IconButton,
  Chip,
  useTheme,
  InputAdornment,
} from '@mui/material';
import {  Edit, Delete, Search } from '@mui/icons-material';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
} from 'material-react-table';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchJobCategories,
  createJobCategory,
  updateJobCategory,
  deleteJobCategory,
  clearJobCategoryError,
} from '@/store';
import { JobCategory, CreateJobCategoryRequest, UpdateJobCategoryRequest } from '@/constants/types';
import { JobCategoryDialog } from './JobCategoryDialog';
import { ConfirmDialog } from '@/components/common';

export const JobCategoryManagement: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const {
    jobCategories,
    pagination,
    isLoading,
    isCreating,
    isUpdating,
    error,
  } = useAppSelector((state) => state.jobCategory);

  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paginationState, setPaginationState] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedJobCategory, setSelectedJobCategory] = useState<JobCategory | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobCategoryToDelete, setJobCategoryToDelete] = useState<JobCategory | null>(null);

  useEffect(() => {
    loadJobCategories();
  }, [globalFilter, statusFilter, paginationState]);

  const loadJobCategories = () => {
    const params: any = {
      page: paginationState.pageIndex + 1,
      limit: paginationState.pageSize,
      sort: '-createdAt',
    };

    if (globalFilter) {
      params.search = globalFilter;
    }

    if (statusFilter !== 'all') {
      params.isActive = statusFilter === 'active';
    }

    dispatch(fetchJobCategories(params));
  };

  const handleCreateClick = () => {
    setSelectedJobCategory(null);
    setDialogOpen(true);
  };

  const handleEditClick = (jobCategory: JobCategory) => {
    setSelectedJobCategory(jobCategory);
    setDialogOpen(true);
  };

  const handleDeleteClick = (jobCategory: JobCategory) => {
    setJobCategoryToDelete(jobCategory);
    setDeleteDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedJobCategory(null);
  };

  const handleDialogSubmit = async (data: CreateJobCategoryRequest | UpdateJobCategoryRequest) => {
    try {
      if (selectedJobCategory) {
        await dispatch(updateJobCategory({ 
          id: selectedJobCategory._id, 
          data: data as UpdateJobCategoryRequest 
        })).unwrap();
      } else {
        await dispatch(createJobCategory(data as CreateJobCategoryRequest)).unwrap();
      }
      handleDialogClose();
      loadJobCategories();
    } catch (error) {
      console.error('Failed to save job category:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (jobCategoryToDelete) {
      try {
        await dispatch(deleteJobCategory(jobCategoryToDelete._id)).unwrap();
        setDeleteDialogOpen(false);
        setJobCategoryToDelete(null);
        loadJobCategories();
      } catch (error) {
        console.error('Failed to delete job category:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setJobCategoryToDelete(null);
  };

  const handleErrorDismiss = () => {
    dispatch(clearJobCategoryError());
  };

  const columns = useMemo<MRT_ColumnDef<JobCategory>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 200,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight="medium">
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        size: 300,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        size: 120,
        enableSorting: true,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue() ? 'Active' : 'Inactive'}
            color={cell.getValue() ? 'success' : 'default'}
            size="small"
          />
        ),
      },
      // {
      //   accessorKey: 'createdAt',
      //   header: 'Created Date',
      //   size: 150,
      //   Cell: ({ cell }) => (
      //     <Typography variant="body2" color="text.secondary">
      //       {new Date(cell.getValue<string>()).toLocaleDateString()}
      //     </Typography>
      //   ),
      // },
      {
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableSorting: false,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => handleEditClick(row.original)}
              sx={{ color: 'primary.main' }}
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(row.original)}
              sx={{ color: 'error.main' }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: jobCategories,
    enableRowSelection: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enablePagination: true,
    enableSorting: true,
    enableBottomToolbar: true,
    enableTopToolbar: false,
    manualPagination: true,
    rowCount: pagination?.totalItems || 0,
    muiTableBodyRowProps: {
      sx: {
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        border: `1px solid ${theme.palette.divider}`,
      },
    },
    muiTableProps: {
      sx: {
        tableLayout: 'fixed',
      },
    },
    state: {
      isLoading,
      pagination: paginationState,
    },
    onPaginationChange: setPaginationState,
  });

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
        Job Categories
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={handleErrorDismiss}>
          {error}
        </Alert>
      )}

      {/* Controls above table */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search job categories..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ minWidth: 300 }}
            size="small"
          />
          
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Box>

        <Button
          variant="contained"
          // startIcon={<Add />}
          onClick={handleCreateClick}
          disabled={isLoading}
        >
          Create Category
        </Button>
      </Box>

      <MaterialReactTable table={table} />

      <JobCategoryDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleDialogSubmit}
        jobCategory={selectedJobCategory}
        isLoading={isCreating || isUpdating}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Job Category"
        message={`Are you sure you want to delete "${jobCategoryToDelete?.name}"? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        severity="error"
      />
    </Box>
  );
};